import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, Modal, Table, Tooltip } from "antd";
import ReportJewelryReportService from "../../../../services/reports-Jewelry-service";
import { BlobToDownloadFile } from "../../../../helper/commanFunction";
import DefaultImage from "../../../../assets/images/icons/sampleimage.jpg";
import { extractDealerIds, mapSortColumn } from "./jewelryHelpers";

// Shared searchable/sortable/paginated product detail table for the jewelry
// reports (dealer scope). Mirrors the diamond suite's DetailSearchTable.jsx
// pattern, adapted to jewelry's columns (product image + Vendor/Retailer/
// Collection/Category/Style/Metal/Price) and its existing
// Admin_JewelryReportForTableChart / CSV_Download endpoints. Full-dataset
// server-side search via @SearchText, added to AdminJewelryReportTableChart
// (+ _Global, _CSV_Download) alongside the diamond suite's search work.
const JewelryDetailSearchTable = ({
  dealerId,
  forDealer,
  dataRangeFrom,
  dataRangeTo,
  vendorIds,
  retailerIds,
  dealerMembershipType,
  filterParamName,
  filterValue,
  metalTypeId,
  metalColorId,
  price1,
  price2,
  fileNamePart,
  dealerCompany,
  refetchToken,
  title,
  reportType,
  // Global Jewelry (Task 2344): the row total is a click count - hide it.
  hideRowCount = false,
}) => {
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortColumnName, setSortColumnName] = useState("Date");
  const [sortType, setSortType] = useState("desc");
  const [visible, setVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const isFirstSearchRender = useRef(true);

  const buildInputData = (pageNumber, size, sortCol, sortOrder, searchText) => ({
    dealerID: dealerId != null ? dealerId.toString() : "",
    forDealer: forDealer ? forDealer.toString() : "",
    fromDate: dataRangeFrom ? moment(dataRangeFrom).format("YYYY-MM-DD") : "2020-08-17",
    toDate: dataRangeTo ? moment(dataRangeTo).format("YYYY-MM-DD") : "2022-09-15",
    retailerIds: extractDealerIds(retailerIds).toString(),
    vendorIds: extractDealerIds(vendorIds).toString(),
    category: filterParamName === "category" ? filterValue || "" : "",
    metalType: filterParamName === "metalType" ? metalTypeId ?? "" : "",
    metalColorType: filterParamName === "metalType" ? metalColorId ?? "" : "",
    price: filterParamName === "price" ? filterValue || "" : "",
    price1: filterParamName === "price" ? price1 ?? "" : "",
    price2: filterParamName === "price" ? price2 ?? "" : "",
    vendor: filterParamName === "vendor" ? filterValue || "" : "",
    retailer: filterParamName === "retailer" ? filterValue || "" : "",
    pageIndex: pageNumber || 1,
    pageSize: size || 15,
    sortColumnName: mapSortColumn(sortCol),
    sortOrder: sortOrder || "desc",
    reporttype: reportType || "Dealer",
    SearchText: searchText || "",
  });

  const fetchData = (pageNumber, size, sortCol, sortOrder, searchText) => {
    if (dealerId == null) return;
    setTableLoading(true);
    ReportJewelryReportService.GetJewelryTableChartData(
      buildInputData(pageNumber, size, sortCol, sortOrder, searchText)
    )
      .then((response) => {
        setTableLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          const responseData = response.data.responseData;
          setTotalCount(responseData?.[0]?.totalRecords || 0);
          setDataSource(responseData || []);
        } else {
          setTotalCount(0);
          setDataSource([]);
        }
      })
      .catch(() => {
        setTableLoading(false);
        setTotalCount(0);
        setDataSource([]);
      });
  };

  // Refetch from page 1 whenever the parent report/filter context changes
  // (chart-slice click, Apply Filter, or the page first becomes ready).
  useEffect(() => {
    setPageNo(1);
    setSearchQuery("");
    fetchData(1, pageSize, sortColumnName, sortType, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, forDealer, filterValue, metalTypeId, metalColorId, price1, price2, refetchToken]);

  // Debounced full-dataset search — searches server-side across every page.
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const handle = setTimeout(() => {
      setPageNo(1);
      fetchData(1, pageSize, sortColumnName, sortType, searchQuery.trim());
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleTableChange = (pagination, _filters, sorter) => {
    const tableBody = document.querySelector(".edr-jewelry-table .ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;

    let type = "desc";
    let column = "date";
    if (sorter.order === "descend") {
      type = "DESC";
      column = sorter.field;
    } else if (sorter.order === "ascend") {
      type = "ASC";
      column = sorter.field;
    }

    setSortType(type);
    setSortColumnName(column);
    setPageNo(pagination.current);
    setPageSize(pagination.pageSize);
    fetchData(pagination.current, pagination.pageSize, column, type, searchQuery.trim());
  };

  const handleExportCSV = () => {
    setCsvLoading(true);
    ReportJewelryReportService.ExportDataToCSV(
      buildInputData(pageNo, pageSize, sortColumnName, "asc", searchQuery.trim())
    )
      .then((response) => {
        setCsvLoading(false);
        const fileName = `${(dealerCompany || "").replace(/\s+/g, "")}_${fileNamePart}_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.csv`;
        BlobToDownloadFile(response.data, fileName);
      })
      .catch(() => {
        setCsvLoading(false);
      });
  };

  const handleExportPDF = () => {
    setPdfLoading(true);
    ReportJewelryReportService.ExportDataToPDF(
      buildInputData(pageNo, pageSize, sortColumnName, "asc", searchQuery.trim())
    )
      .then((response) => {
        setPdfLoading(false);
        const fileName = `${(dealerCompany || "").replace(/\s+/g, "")}_${fileNamePart}_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.pdf`;
        BlobToDownloadFile(response.data, fileName, "pdf");
      })
      .catch(() => {
        setPdfLoading(false);
      });
  };

  const imageClickHandler = (img) => {
    setSelectedImage(img);
    setVisible(true);
  };

  const isVendor = dealerMembershipType === "Vendor" || dealerMembershipType === "VendorDiamond" || dealerMembershipType === "VendorGemstone";
  const isRetailer = dealerMembershipType === "Retailer";

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <div>
          <h3>{title || t("Product Click Log")}</h3>
          <div className="edr-panel-desc">{t("Individual product views · click a column to sort")}</div>
        </div>
        <div className="edr-pagehead-actions">
          <Button className="ghost-btn" onClick={handleExportCSV} loading={csvLoading}>
            <i className="fa fa-download"></i> {t("CSV")}
          </Button>
          <Button className="ghost-btn" onClick={handleExportPDF} loading={pdfLoading}>
            <i className="fa fa-download"></i> {t("PDF")}
          </Button>
        </div>
      </div>

      <div className="edr-tblbar">
        <input
          className="edr-tblsearch"
          placeholder={t("Search vendor, retailer, category, style…")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {!hideRowCount && <span className="edr-badge">{t("{{count}} rows", { count: totalCount })}</span>}
      </div>

      <div className="edr-detail-table edr-jewelry-table">
        <Table
          className="pendjewelreq__tbl"
          scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
          loading={tableLoading}
          columns={[
            {
              title: <Tooltip title={t("Product Image")}>{t("Product Image")}</Tooltip>,
              dataIndex: "imagePathNew",
              render: (img) => (
                <span onClick={() => imageClickHandler(img || DefaultImage)} style={{ cursor: "pointer" }}>
                  <img src={img || DefaultImage} alt="" className="product__img" />
                </span>
              ),
            },
            { title: <Tooltip title={t("User IP Address")}>{t("User IP Address")}</Tooltip>, ellipsis: true, dataIndex: "userIPAddress", sorter: () => {} },
            ...(!isVendor
              ? [{ title: <Tooltip title={t("Vendor")}>{t("Vendor")}</Tooltip>, ellipsis: true, dataIndex: "vendorName", sorter: () => {} }]
              : []),
            ...(!isRetailer
              ? [{ title: <Tooltip title={t("Retailer")}>{t("Retailer")}</Tooltip>, ellipsis: true, dataIndex: "retailerName", sorter: () => {} }]
              : []),
            { title: <Tooltip title={t("Collection")}>{t("Collection")}</Tooltip>, ellipsis: true, dataIndex: "collectionNew", sorter: () => {} },
            { title: <Tooltip title={t("Category")}>{t("Category")}</Tooltip>, ellipsis: true, dataIndex: "category", sorter: () => {} },
            {
              title: <Tooltip title={t("Date")}>{t("Date")}</Tooltip>,
              ellipsis: true,
              dataIndex: "date",
              defaultSortOrder: "descend",
              sorter: () => {},
              render: (date) => date && moment(date).format("MM-DD-YYYY, hh:mm:ss A"),
            },
            { title: <Tooltip title={t("Style Number")}>{t("Style Number")}</Tooltip>, ellipsis: true, dataIndex: "styleNumber", sorter: () => {} },
            { title: <Tooltip title={t("Metal Type")}>{t("Metal Type")}</Tooltip>, ellipsis: true, dataIndex: "metalType", sorter: () => {} },
            { title: <Tooltip title={t("Metal Color")}>{t("Metal Color")}</Tooltip>, ellipsis: true, dataIndex: "metalColor", sorter: () => {} },
            {
              title: <Tooltip title={t("Price")}>{t("Price")}</Tooltip>,
              ellipsis: true,
              dataIndex: "price",
              sorter: () => {},
              render: (price) => <Tooltip title={price}><span>{price}</span></Tooltip>,
            },
          ]}
          dataSource={dataSource}
          pagination={{
            size: "small",
            total: totalCount,
            pageSize: pageSize,
            pageSizeOptions: [10, 15, 20, 50, 100],
            responsive: true,
            showSizeChanger: true,
            current: pageNo,
          }}
          onChange={handleTableChange}
        />
      </div>

      <Modal title="" centered visible={visible} onOk={() => setVisible(false)} onCancel={() => setVisible(false)} width={500} footer={false} className="image-modal">
        <img src={selectedImage} alt="" className="img-fluid" />
      </Modal>
    </div>
  );
};

export default JewelryDetailSearchTable;
